// ─── Menu d'onglet (appui long sur la barre du bas) ───────────────
// Permet à l'écran actif d'enregistrer un menu déroulant pour SON onglet.
// La barre d'onglets (CoquilleApp) l'ouvre sur appui long (cf. mémoire
// « bascules-barre-du-bas »). Exemple : dans un inventaire ouvert, l'écran
// enregistre { Inventaires: [{ label:'Configuration', onPress: … }] } ; un
// appui long sur l'onglet « Inventaires » déroule ce menu.
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

const Ctx = createContext(null);

export function MenuOngletProvider({ children }) {
  const [menus, setMenus] = useState({});   // { [cle]: [{ label, icone, onPress }] }
  const [doubleTaps, setDoubleTaps] = useState({}); // { [cle]: fn } — double-clic sur l'onglet
  const [resets, setResets] = useState({}); // { [cle]: n } — compteur incrémenté au clic d'onglet
  const signalReset = useCallback((cle) => setResets((m) => ({ ...m, [cle]: (m[cle] || 0) + 1 })), []);
  const definirMenu = useCallback((cle, items) => {
    setMenus((m) => {
      const next = items && items.length ? items : null;
      if ((m[cle] || null) === next) return m;
      const copie = { ...m };
      if (next) copie[cle] = next; else delete copie[cle];
      return copie;
    });
  }, []);
  const definirDoubleTap = useCallback((cle, fn) => {
    setDoubleTaps((m) => {
      if ((m[cle] || null) === (fn || null)) return m;
      const copie = { ...m };
      if (fn) copie[cle] = fn; else delete copie[cle];
      return copie;
    });
  }, []);
  const value = useMemo(() => ({ menus, definirMenu, doubleTaps, definirDoubleTap, resets, signalReset }),
    [menus, definirMenu, doubleTaps, definirDoubleTap, resets, signalReset]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useMenuOnglet() {
  return useContext(Ctx) || { menus: {}, definirMenu: () => {}, doubleTaps: {}, definirDoubleTap: () => {}, resets: {}, signalReset: () => {} };
}

// Exécute `fn` (ex. fermer les modales et revenir à la page) quand l'utilisateur
// re-touche l'onglet `cle`. Ignore le montage initial.
export function useResetOnglet(cle, fn) {
  const { resets } = useMenuOnglet();
  const ref = useRef(fn); ref.current = fn;
  const n = resets[cle] || 0;
  const prem = useRef(true);
  useEffect(() => {
    if (prem.current) { prem.current = false; return; }
    ref.current && ref.current();
  }, [n]);
}

// Enregistre une action de DOUBLE-CLIC sur l'onglet `cle` (ex. ouvrir le
// sélecteur de calendriers) tant que le composant est monté et `actif`.
export function useDoubleTapOngletActif(cle, fn, actif = true) {
  const { definirDoubleTap } = useMenuOnglet();
  const ref = useRef(fn);
  ref.current = fn;
  useEffect(() => {
    if (!actif || !fn) { definirDoubleTap(cle, null); return undefined; }
    definirDoubleTap(cle, () => ref.current && ref.current());
    return () => definirDoubleTap(cle, null);
  }, [cle, actif, !!fn]); // eslint-disable-line react-hooks/exhaustive-deps
}

// Enregistre `items` pour l'onglet `cle` tant que le composant est monté et
// que `actif` est vrai (nettoyé au démontage). Les `onPress` sont figés dans
// une ref pour rester frais sans re-déclencher l'effet à chaque rendu.
export function useMenuOngletActif(cle, items, actif = true) {
  const { definirMenu } = useMenuOnglet();
  const ref = useRef(items);
  ref.current = items;
  // Signature stable : libellés + icônes (les fonctions changent d'identité
  // à chaque rendu, on ne veut pas ré-enregistrer pour rien).
  const sig = actif && items ? items.map((i) => `${i.separateur ? '—' : ''}${i.entete || ''}${i.icone || ''}${i.label || ''}`).join('|') : '';
  useEffect(() => {
    if (!actif || !items || !items.length) { definirMenu(cle, null); return undefined; }
    // On enregistre des proxys qui délèguent à la ref (toujours à jour).
    // On conserve les champs de navigation déclarative (ex. `navigateVers`).
    definirMenu(cle, items.map((i, idx) => ({
      ...i,
      onPress: () => ref.current?.[idx]?.onPress?.(),
    })));
    return () => definirMenu(cle, null);
  }, [cle, sig]); // eslint-disable-line react-hooks/exhaustive-deps
}
